import { DocumentNode } from "graphql";
import {
  MY_HR_COMPANIES_SUBSCRIPTION,
  MY_HR_CONTRACTS_SUBSCRIPTION,
  MY_HR_GET_COMPANIES_ADMIN_LIST,
  MY_HR_GET_CONTRACTS_ADMIN_LIST,
  MY_HR_GET_FILTERED_COMPANIES,
  MY_HR_GET_IMPORT_EMPLOYEE_LIST,
  MY_HR_GET_JOB_CLASS_GROUPS,
  MY_HR_GET_JOB_CLASS_GROUPS_ADMIN_LIST,
  MY_HR_GET_ORG_UNITS,
  MY_HR_GET_ORG_UNITS_ADMIN_LIST,
  MY_HR_GET_PAY_OFFICES,
  MY_HR_GET_PAY_OFFICES_ADMIN_LIST,
  MY_HR_GET_SHORT_CONTRACT_LIST,
  MY_HR_GET_WORKING_PLACES,
  MY_HR_GET_WORKING_PLACES_ADMIN_LIST,
  MY_HR_JOB_CLASS_GROUPS_SUBSCRIPTION,
  MY_HR_ORG_UNITS_SUBSCRIPTION,
  MY_HR_PAY_OFFICES_SUBSCRIPTION,
  MY_HR_WORKING_PLACES_SUBSCRIPTION,
} from "my-hr/graphql";
import { MyTimesheetFilterSubtypeProps } from "my-timesheet/components";
import {
  MY_TIMESHEET_CLIENTS_SUBSCRIPTION,
  MY_TIMESHEET_COSTS_SUBSCRIPTION,
  MY_TIMESHEET_GET_CLIENTS_LIST,
  MY_TIMESHEET_GET_COSTS_LIST,
  MY_TIMESHEET_GET_PROJECT_LIST,
  MY_TIMESHEET_PROJECTS_SUBSCRIPTION,
} from "my-timesheet/graphql";
import { OcRoleFilterSubtypeProps } from "oc/components";
import { RoleFilterInput } from "oc/types";

// #region OC part

/**
 *   This contains all the different queries to call,
 * based on queryType and then based on type.
 */
export const OC_ROLE_FILTER_QUERIES: Record<
  OcRoleFilterSubtypeProps["queryType"],
  Record<keyof RoleFilterInput, DocumentNode>
> = {
  admin: {
    company: MY_HR_GET_COMPANIES_ADMIN_LIST,
    orgUnit: MY_HR_GET_ORG_UNITS_ADMIN_LIST,
    workingPlace: MY_HR_GET_WORKING_PLACES_ADMIN_LIST,
    jobClassGroup: MY_HR_GET_JOB_CLASS_GROUPS_ADMIN_LIST,
    payOffice: MY_HR_GET_PAY_OFFICES_ADMIN_LIST,
    contract: MY_HR_GET_CONTRACTS_ADMIN_LIST,
  },
  notAdmin: {
    company: MY_HR_GET_FILTERED_COMPANIES,
    orgUnit: MY_HR_GET_ORG_UNITS,
    workingPlace: MY_HR_GET_WORKING_PLACES,
    jobClassGroup: MY_HR_GET_JOB_CLASS_GROUPS,
    payOffice: MY_HR_GET_PAY_OFFICES,
    contract: MY_HR_GET_SHORT_CONTRACT_LIST,
  },
  import: {
    company: MY_HR_GET_COMPANIES_ADMIN_LIST,
    orgUnit: MY_HR_GET_ORG_UNITS_ADMIN_LIST,
    workingPlace: MY_HR_GET_WORKING_PLACES_ADMIN_LIST,
    jobClassGroup: MY_HR_GET_JOB_CLASS_GROUPS_ADMIN_LIST,
    payOffice: MY_HR_GET_PAY_OFFICES_ADMIN_LIST,
    contract: MY_HR_GET_IMPORT_EMPLOYEE_LIST,
  },
} as const;

// This contains all the different subscriptions, based on the type of the filter.
export const OC_ROLE_FILTER_SUBSCRIPTIONS: Record<
  keyof RoleFilterInput,
  DocumentNode
> = {
  company: MY_HR_COMPANIES_SUBSCRIPTION,
  orgUnit: MY_HR_ORG_UNITS_SUBSCRIPTION,
  workingPlace: MY_HR_WORKING_PLACES_SUBSCRIPTION,
  jobClassGroup: MY_HR_JOB_CLASS_GROUPS_SUBSCRIPTION,
  payOffice: MY_HR_PAY_OFFICES_SUBSCRIPTION,
  contract: MY_HR_CONTRACTS_SUBSCRIPTION,
} as const;

/** The rolefilters must be passed through the queryParams, but the key is
 * based on the type of the filter.
 *
 * For example, `companies: roleFilters[type]` wouldn't work when trying to get the org units.
 * In that case, it would/should be `orgUnits: roleFilters[type]`
 */
export const OC_ROLE_FILTER_ENTITY_DEFINITIONS = {
  company: "companies",
  orgUnit: "orgUnits",
  workingPlace: "workingPlaces",
  jobClassGroup: "jobClassGroups",
  payOffice: "payOffices",
  contract: "contracts",
} as const satisfies Record<keyof RoleFilterInput, string>;

/** The query returns data like this: `data.filteredCompanies.availableList`.
 *
 * It changes based on the queryType and the filter type.
 *
 * This contains all the different values the key could be.
 */
export const OC_ROLE_FILTER_RETURN_ENTITY_DEFINITIONS = {
  admin: {
    company: "adminListCompanies",
    orgUnit: "orgUnitsAdminList",
    workingPlace: "workingPlacesAdminList",
    jobClassGroup: "jobClassGroupsAdminList",
    payOffice: "payOfficesAdminList",
    contract: "contractsAdminList",
  },
  notAdmin: {
    company: "filteredCompanies",
    orgUnit: "orgUnits",
    workingPlace: "workingPlaces",
    jobClassGroup: "jobClassGroups",
    payOffice: "payOffices",
    contract: "shortContractList",
  },
  import: {
    company: "adminListCompanies",
    orgUnit: "orgUnitsAdminList",
    workingPlace: "workingPlacesAdminList",
    jobClassGroup: "jobClassGroupsAdminList",
    payOffice: "payOfficesAdminList",
    contract: "importEmployeeList",
  },
} as const satisfies Record<
  OcRoleFilterSubtypeProps["queryType"],
  Record<keyof RoleFilterInput, string>
>;
// #endregion

// #region MyTimesheet part

// The reasons these are needed are written above.
export const MY_TIMESHEET_FILTER_QUERIES: Record<
  MyTimesheetFilterSubtypeProps["type"],
  DocumentNode
> = {
  project: MY_TIMESHEET_GET_PROJECT_LIST,
  client: MY_TIMESHEET_GET_CLIENTS_LIST,
  cost: MY_TIMESHEET_GET_COSTS_LIST,
} as const;

export const MY_TIMESHEET_FILTER_SUBSCRIPTIONS: Record<
  MyTimesheetFilterSubtypeProps["type"],
  DocumentNode
> = {
  project: MY_TIMESHEET_PROJECTS_SUBSCRIPTION,
  client: MY_TIMESHEET_CLIENTS_SUBSCRIPTION,
  cost: MY_TIMESHEET_COSTS_SUBSCRIPTION,
} as const;

export const MY_TIMESHEET_FILTER_ENTITY_DEFINITONS = {
  project: "listMyTimesheetProjects",
  client: "listMyTimesheetClients",
  cost: "listMyTimesheetCosts",
} as const satisfies Record<MyTimesheetFilterSubtypeProps["type"], string>;
// #endregion
